import { TimezoneOption } from "@core/features/timezones";
import { DownloadICSButton } from "@themes/pollinator/ui/DownloadICSButton";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { Meeting } from "@platform/types/platform.types";
import type { Action, SelectableOption } from "@core/features/actions";
import { ProgramCalendarEventItem } from "../ProgramCalendarEventItem";
import { CalendarEventTimeItem } from "../CalendarEventTimeItem";

export type Props = {
  /** Meetings to display (takes precedence over selectableOptions) */
  meetings?: Meeting[];
  /** Scheduled time options from display_options (used when no meetings) */
  selectableOptions?: SelectableOption[];
  /** Title for calendar events when using selectableOptions */
  eventTitle?: string;
  /** Description for calendar events when using selectableOptions */
  eventDescription?: string;
  /** User's timezone for display */
  userTimezone?: TimezoneOption;
  /** Callback when calendar event is added */
  onAddCalendarEvent: (
    eventId: number | string | "all",
    calendarType: "google" | "ical"
  ) => void;
  /** ID of the current/active meeting (closest to now) */
  currentMeetingId?: number;
  /** ID of the currently expanded meeting */
  expandedMeetingId?: number | null;
  /** Toggle expand/collapse for a meeting */
  onToggleMeeting?: (meetingId: number) => void;
  /** Actions grouped by meeting ID (binding_object_id) */
  actionsByMeetingId?: Record<number, Action[]>;
  /** Meeting attend action by meeting ID (data source for meeting card) */
  meetingActionByMeetingId?: Record<number, Action>;
};

export function ProgramCalendarEventList({
  meetings,
  selectableOptions,
  eventTitle = "Peer Circle Meeting",
  eventDescription,
  userTimezone,
  onAddCalendarEvent,
  currentMeetingId,
  expandedMeetingId,
  onToggleMeeting,
  actionsByMeetingId,
  meetingActionByMeetingId,
}: Props) {
  const hasMeetings = meetings && meetings.length > 0;
  const hasOptions = selectableOptions && selectableOptions.length > 0;

  // Return null if no data to display
  if (!hasMeetings && !hasOptions) return null;

  return (
    <div className="flex flex-col gap-12">
      {hasOptions && (
        <div className="flex flex-col gap-6">
          {selectableOptions.map((option) => (
            <CalendarEventTimeItem
              key={option.id || `${option.date}-${option.startHour}`}
              option={option}
              eventTitle={eventTitle}
              eventDescription={eventDescription}
              userTimezone={userTimezone}
              onAddCalendarEvent={onAddCalendarEvent}
            />
          ))}
        </div>
      )}

      {hasMeetings && (
        <SectionHeading
          title="Meetings"
          description="View your upcoming meetings."
        >
          <div className="flex flex-col gap-3">
            {meetings.map((meeting) => (
              <ProgramCalendarEventItem
                key={meeting.id}
                meeting={meeting}
                userTimezone={userTimezone}
                onAddCalendarEvent={onAddCalendarEvent}
                isActive={meeting.id === currentMeetingId}
                isExpanded={expandedMeetingId === meeting.id}
                onToggle={() => onToggleMeeting?.(meeting.id)}
                meetingActions={actionsByMeetingId?.[meeting.id]}
                meetingAction={meetingActionByMeetingId?.[meeting.id]}
              />
            ))}

            {meetings.length > 1 && (
              <div className="flex">
                <DownloadICSButton
                  meetings={meetings}
                  eventTitle="Meeting"
                  onAddCalendarEvent={onAddCalendarEvent}
                  variant="ghost"
                />
              </div>
            )}
          </div>
        </SectionHeading>
      )}
    </div>
  );
}

export default ProgramCalendarEventList;
